import React from 'react';
import { NavLink } from 'react-router-dom';
import { Home, FileText, Search, User, LogOut, CheckCircle, Database, Users, TestTube, BrainCircuit, Settings, Server, UserCog, BarChart } from 'lucide-react';
import useAuthStore from 'stores/authStore';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuthStore();

  return (
    <div className="flex h-screen bg-background-light">
      <aside className="w-64 flex-shrink-0 bg-primary text-white flex flex-col">
        {/* Logo Section - Top of side menu */}
        <div className="flex-shrink-0">
          <div className="flex items-center justify-center">
            <img
              src="/topSideMenu.svg"
              alt="CoGenAI Logo"
              className="w-full h-auto"
            />
          </div>
        </div>

        {/* Scrollable navigation section */}
        <div className="flex-1 overflow-y-auto">
          <nav>
            {/* RESEARCH & ANNOTATION TOOLS - Available to all authenticated users */}
            <div className="p-4 text-sm font-semibold text-gray-400">RESEARCH TOOLS</div>
            <NavLink to="/" className="flex items-center p-4 hover:bg-primary-dark">
              <Home className="mr-3" />
              Home
            </NavLink>
            <NavLink to="/documents" className="flex items-center p-4 hover:bg-primary-dark">
              <FileText className="mr-3" />
              Documents
            </NavLink>
            <NavLink to="/validate" className="flex items-center p-4 hover:bg-primary-dark">
              <CheckCircle className="mr-3" />
              Validate QA Pairs
            </NavLink>
            <NavLink to="/playground" className="flex items-center p-4 hover:bg-primary-dark">
              <TestTube className="mr-3" />
              Model Playground
            </NavLink>

            {/* ADMINISTRATION - Available to admin and super_admin */}
            {['admin', 'super_admin'].includes(user?.role?.toLowerCase() || '') && (
              <>
                <div className="p-4 mt-4 text-sm font-semibold text-gray-400">ADMINISTRATION</div>
                <NavLink to="/admin/users" className="flex items-center p-4 hover:bg-primary-dark">
                  <User className="mr-3" />
                  User Management
                </NavLink>
                <NavLink to="/admin/tasks" className="flex items-center p-4 hover:bg-primary-dark">
                  <Users className="mr-3" />
                  Task Assignment
                </NavLink>
                <NavLink to="/admin/models" className="flex items-center p-4 hover:bg-primary-dark">
                  <BrainCircuit className="mr-3" />
                  Model Management
                </NavLink>
              </>
            )}

            {/* SYSTEM - Exclusive to super_admin only */}
            {user?.role?.toLowerCase() === 'super_admin' && (
              <>
                <div className="p-4 mt-4 text-sm font-semibold text-gray-400">SYSTEM</div>
                <NavLink to="/admin/system" className="flex items-center p-4 hover:bg-primary-dark">
                  <Server className="mr-3" />
                  System Dashboard
                </NavLink>

                <NavLink to="/admin/analytics" className="flex items-center p-4 hover:bg-primary-dark">
                  <BarChart className="mr-3" />
                  Analytics
                </NavLink>
                <NavLink to="/admin/settings" className="flex items-center p-4 hover:bg-primary-dark">
                  <Settings className="mr-3" />
                  System Settings
                </NavLink>
              </>
            )}
          </nav>
        </div>

        {/* Footer Section */}
        <div className="p-4 flex-shrink-0">
          <div className="text-xs text-gray-400 text-center">
            © {new Date().getFullYear()} PLESMOSENSE | Last updated: {new Date().toLocaleString()}
          </div>
        </div>
      </aside>
      <div className="flex flex-1 flex-col">
        <header className="flex items-center justify-between bg-surface p-4 shadow-md flex-shrink-0">
          <div></div>
          <div className="flex items-center space-x-4">
            {(() => {
              console.log('👤 Layout Rendering. User in Store:', user?.name, 'Role:', user?.role);
              return null;
            })()}
            <span className="mr-4">Welcome, {user?.name}</span>
            <NavLink
              to="/profile"
              className="flex items-center text-text-secondary hover:text-text-primary"
            >
              <UserCog className="mr-2" />
              Profile
            </NavLink>
            <button onClick={logout} className="flex items-center text-text-secondary hover:text-text-primary">
              <LogOut className="mr-2" />
              Logout
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
};

export default Layout;